/**
 * PipelineBoard.tsx — the 11-stage CRM board (2026-07-21, PR 3/5).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * Generalizes the proven SwimlaneBoard grammar (native HTML5 DnD, per-lane
 * live counts, independent column scroll, gated drops) onto deals. A drop is
 * validated against the stages.ts MANUAL_TARGETS mirror (affordance — the
 * server re-validates, R41) and ROUTED, never applied directly: every legal
 * move goes back up through `onRequestMove` so the owning dialog (handover
 * note / convert / closed-lost reason) can run first. System-only lanes
 * (Clinical review, Won) and terminal cards are never draggable.
 *
 * CARD LAYOUT — FOUR FIXED ROWS (CTO, 2026-09-06). Everything after the name
 * used to be one wrapping meta row, so a busy card decided its own shape and no
 * two cards in a lane put the same fact in the same place:
 *
 *   Row 1  the contact's name in bold — a nameless historical record shows a
 *          MUTED "Unknown" placeholder (never the literal "undefined", never
 *          the email standing in as a name), prefixed by `[Duplicate]` on a
 *          merged card — and, pushed hard RIGHT, the SOURCE chip and then the
 *          stage-age chip. ⚠️ The source chip is deliberately NOT inside the
 *          age chip's `showStageAgeCue` guard: that guard is false on every
 *          terminal stage, and the source icon must render on all eleven.
 *   Row 2  the email, muted and smaller — ONE LINE, ellipsised, with the whole
 *          address on `title` so hover and assistive tech still get it.
 *   Row 3  the clinical chips — New, Test, the icon-led CLINICAL STATUS chip
 *          (2026-08-26 §3; the bare eligibility badge is its fallback), the
 *          clinical-flags mark, "Plan sent", "Action needed", and last the
 *          icon-only "Review parked" pause chip.
 *   Row 4  the created-on date, ruled off with a hairline so it reads as a
 *          footer rather than as a fourth fact.
 *
 * A ⏰ scheduled-callback line sits between rows 3 and 4 when the deal has one.
 * The card carries a min-height so nothing mid-card ever clips.
 *
 * ── ONE CARD, ONE HEIGHT (2026-09-06, CTO) ──────────────────────────────────
 *
 * Two things made cards jump, and each is fixed at its own end:
 *   · a long email WRAPPED to a second line, so card height depended on whose
 *     mail provider had the longer name. Row 2 is now `nowrap` + ellipsis, and
 *     `title` keeps the full address hoverable and in the accessibility tree.
 *   · row 3 VANISHED on a card with nothing to put in it. It is now always
 *     rendered with a `minHeight` of exactly one chip (`CHIP_HEIGHT`), so an
 *     empty status row is the same height as a full one.
 *
 * ── AND THE SLOT GETS FILLED (2026-09-06, CTO — the real defect) ─────────────
 *
 * Reserving the row fixed the ragged heights; it did not fix what the empty
 * row MEANT. `clinicalStatusFor` returns null on a card nobody has decided
 * anything about, so an ELIGIBLE, UNFLAGGED patient waiting for a clinician's
 * signature rendered no chip — visually identical to a patient who never
 * started the questionnaire. On the FIRST THREE LANES only, that slot now
 * carries `preDecisionChipFor`: a questionnaire ghost (not started / in
 * progress) or a two-half REVIEW chip whose verdict half says clear, flagged
 * or not eligible. Later lanes have a decision behind them and keep exactly
 * the chips they had. See statusChips.tsx for the derivation, for why the
 * amber half never changes, and for the `hasClinicalFlags === undefined` trap.
 *
 * CARD INDICATORS (the CTO's urgency/rotting cues): the `Nd` age chip — LIFETIME
 * on the face, warn/danger tone once `rotting` (server-computed vs
 * PIPELINE_ROT_DAYS) plus a highlighted card border, both suppressed on a
 * TERMINAL card (see showStageAgeCue); the entry-path tag ("show the option
 * they took") — since 2026-09-06 a compact ICON chip for ALL FOUR provenance
 * lanes (page / handset / person), with the full wording as title + aria-label,
 * see statusChips.tsx; and (2026-08-26) a "Plan sent" chip on approved cards
 * whose LANE cannot say it — see showPlanSentChip.
 *
 * ⚠️ NOT ON THE CARD, and each absence is a decision somebody made for a
 * reason: the per-owner call-attempt counters and the "Call limit" threshold
 * warning (both live in the drawer's Activity tab now — see the block where
 * they used to render for what that costs), and the free-text closure REASON
 * (it restated the eligibility chip's own verdict in red; it rides that chip's
 * tooltip and the card's `title` now, and the drawer quotes it in full with its
 * author). NO money anywhere ON A CARD, for either role — see the lane
 * footer below for the one place money does appear, and why it is not here.
 *
 * LANE MONEY (2026-09-06, CTO): the LANE has a money FOOTER — ADMIN only, never
 * a card, never a clinician. It is aggregated SERVER-SIDE into one figure per
 * lane precisely so no per-card price ever reaches the browser to be stripped
 * per role; a per-card price would make the leak a rendering mistake away.
 * See `laneMoney.tsx`. The model: a clinician's APPROVAL is the economic event
 * — we owe them for it whether or not the patient pays — so an approved card
 * carries value in EVERY lane, and each lane says a different thing about it
 * (won = realised, one month; lost = forfeited revenue AND the clinician fees
 * we owe anyway, never summed; everything else = pending).
 *
 * It is a DEDICATED ROW (2026-09-07, CTO): for an ADMIN every lane carries a
 * cell, tinted, at the same level along the foot of the board, so the eye can
 * run along it — the HubSpot reading. An empty lane is £0 and says so; a lane
 * the server did not send, and a lane whose money source is down, say that
 * instead, because an unreachable service and an empty lane produce the same
 * number and only one of them is a fact. For a CLINICIAN the row does not
 * exist at all.
 *
 * PER-LANE PAGING (2026-09-04): each lane's foot carries a "View more" control
 * that loads the NEXT BOARD_PER_LANE cards for that lane alone (clinic-service
 * `offset`, which pages one lane rather than the board). The loaded pages
 * ACCUMULATE — PipelineTab owns the bookkeeping and hands this component the
 * merged list — and the control refuses itself in exactly the two states the
 * "N of M" count cue refuses itself in; see `laneViewMore`.
 *
 * CLOSED CARDS carried their CLOSURE REASON as a red line under the heading
 * block from 2026-08-27 (stage clarity §1) until 2026-09-06. The problem it
 * solved was real — the Lost column had been a stack of names above a derived
 * "Rejected" chip that could not tell an engine hard stop from a clinician's
 * decision — but the line double-stated the commonest case: "not eligible
 * (bmi_floor)" in danger red directly above a chip already reading NOT
 * ELIGIBLE. The fact is kept and the duplication is not; see the eligibility
 * chip in row 3 and the card's `title`.
 */
import { useEffect, useRef, useState } from 'react';
import { BOARD_PER_LANE } from '../api/deals';
import type { DealCard as DealCardShape, DealCounts, DealLaneMoneyResponse, DealStage, UserRole } from '../api/types';
import { formatUtcDate } from '../lib/format';
import { formatCallWindow } from '../lib/timeBlocks';
import { Alert, Badge } from '../ui/kit';
import { toneFor } from '../ui/tones';
import {
  CHIP_HEIGHT,
  ClinicalStatusChip,
  EntryPathChip,
  QuestionnaireGhostChip,
  ReviewParkedChip,
  clinicalStatusFor,
  isReviewParked,
  preDecisionChipFor,
} from './statusChips';
import { DuplicateMarker, isMergedDuplicate } from './duplicateSurface';
import { ReturningChipRow, SubmittedAsLine, SupersededMarker, cardFooterText, isSuperseded } from './returning';
import { LaneMoneyFooter } from './laneMoney';
import {
  ActionNeededChip,
  ClinicalFlagsChip,
  NewDealChip,
  cardActionNeeded,
  cardClosedReason,
  cardHasClinicalFlags,
  cardIsNew,
} from './stageClarity';
import { DropOffReasonChip, dropOffChipFor } from './dropOffReasonsStaff';
import { edgeScrollStep } from './boardEdgeScroll';
import {
  DEAL_STAGES,
  // REQUIRED_ROUND_ATTEMPTS is deliberately NOT imported here any more — see the
  // note where the "Attempt N/3" chip used to render. The drawer and the
  // activity composer are the two surfaces that still state the ceiling.
  STAGE_FOCUS,
  STAGE_LABEL,
  TERMINAL_STAGES,
  isAwaitingClinicalDecision,
  manualTarget,
  movesFor,
  type ManualTarget,
} from './stages';

const cardStyle = (rotting: boolean, focus: boolean) =>
  ({
    display: 'grid',
    gap: '3px',
    width: '100%',
    /**
     * ⚠️⚠️ THERE IS NO `minHeight` HERE ANY MORE, AND THE 88px THAT USED TO BE
     * IS THE WHOLE OF THE "cards are cramped / the email row is missing" DEFECT
     * (2026-09-07, CTO boxed six cards on the live board).
     *
     * It read `minHeight: '88px'` with a comment saying "room for name + email +
     * badges + date — nothing mid-card ever clips". It did the exact opposite,
     * and here is the mechanism, measured in a browser rather than reasoned:
     *
     *   1. A LANE IS A FIXED-HEIGHT GRID (`LANE_MAX_HEIGHT`, `display: grid`,
     *      `overflow-y: auto` — see the lane scroller below). Its rows are
     *      implicit `auto` tracks, so each card's track is sized between the
     *      card's MINIMUM contribution and its max-content height.
     *   2. AN EXPLICIT `min-height` ON A GRID ITEM *IS* ITS MINIMUM
     *      CONTRIBUTION. So each card's track had a base size of 88px while the
     *      four-row card actually needs ~119px.
     *   3. WHEN THE LANE'S CARDS EXCEED ITS HEIGHT — seven cards in a 467px
     *      lane, i.e. every busy lane on the real board — there is no free space
     *      to distribute, so every track stays AT ITS BASE SIZE. Each card
     *      rendered at 88px instead of 119px.
     *   4. INSIDE AN 88px CARD the four rows have 72px of content box to fit
     *      101px of rows, so the same negative-free-space rule runs one level
     *      down and each ROW sits at its own base size. Three of the four rows
     *      have a real minimum (the chip row's `CHIP_HEIGHT`, the name row's
     *      chips, the footer's text). The EMAIL row's minimum is ZERO, because
     *      `overflow: hidden` — which it carries to get its ellipsis — sets a
     *      grid item's automatic minimum size to zero.
     *   5. So the email row collapsed to 0px and its text was clipped away
     *      ENTIRELY. Measured `grid-template-rows` on a card in a full lane:
     *      `26.5px 0px 22px 25.25px`. The same card in a SHORT lane, where the
     *      grid has slack: `26.5px 18.75px 22px 25.25px`.
     *
     * That is exactly what the CTO photographed: the cards he marked BAD were
     * the ones in busy lanes (no email row, everything packed, card shorter
     * than its neighbours) and the three he marked GOOD were in lanes with room
     * to spare. Nobody's email was missing from the data — the row was being
     * crushed, and the floor that was supposed to prevent clipping was the thing
     * doing the crushing.
     *
     * WHAT REPLACES IT: nothing, deliberately. Every row now declares its own
     * minimum (row 2 gets `CARD_META_LINE`, row 3 already had `CHIP_HEIGHT`),
     * so the card's minimum contribution IS the sum of its reserved rows and
     * cannot drift below the content the way a typed 88px did. A floor derived
     * from the rows it reserves is the only kind that stays true when a row is
     * added — and this one has now been wrong twice, since the four-row card
     * landed on 2026-09-06 and nobody re-measured the number from Wave 4.
     */
    alignContent: 'start',
    textAlign: 'left',
    background: 'var(--gps-surface)',
    border: rotting ? '1.5px solid var(--gps-warn-text)' : '1px solid var(--gps-border-strong)',
    borderRadius: 'var(--gps-r-md)',
    padding: '8px 10px',
    font: 'inherit',
    cursor: 'pointer',
    ...(focus ? {} : {}),
  }) as const;

const metaStyle = { fontSize: '12.5px', color: 'var(--gps-muted)' } as const;
/** The created-on date line — deliberately the quietest thing on the card. */
const dateStyle = { fontSize: '11.5px', color: 'var(--gps-muted)' } as const;

/* ── THE FOUR-ROW CARD (2026-09-06, CTO) ────────────────────────────────────
 *
 * Everything after the name used to be ONE wrapping meta row, so a busy card
 * decided its own shape: the tags took two or three lines, the date moved, and
 * no two cards in a lane put the same fact in the same place. A scan surface
 * only works if the eye can learn ONE map and reuse it.
 *
 *   Row 1  name (left) ······ source chip · age chip (right)
 *   Row 2  email
 *   Row 3  the clinical status chips
 *   Row 4  created on, ruled off as a footer
 *
 * Rows 1 and 3 are the only ones with geometry worth naming; 2 and 4 are single
 * spans. The constants live here beside `cardStyle` so the card's whole layout
 * is readable in one place rather than as five inline literals 300 lines down.
 */

/** ROW 1 — identity hard left, the card's two qualifiers hard right.
 *
 *  ⚠️ `minWidth: 0` HERE TOO, not just on the identity child below. A flex
 *  item's automatic minimum size is its CONTENT, and once row 1 stopped
 *  wrapping, that content became the whole unbroken name string. Measured in
 *  Chrome: a 26-character surname made the CARD 411.8px inside a 272px lane,
 *  so the lane grew its own horizontal scrollbar and the name never
 *  ellipsised — the truncation on `cardIdentityStyle` cannot fire while an
 *  ancestor is free to grow to fit. With the zero: lane scrollWidth 420 → 280,
 *  card back to 272px, name truncates, no card height changes. */
const cardRowOneStyle = { display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', minWidth: 0 } as const;

/** Row 1, left: the duplicate marker and the name TOGETHER. `minWidth: 0` is
 *  what lets a long name ellipsize instead of shoving the chips off the card. */
const cardIdentityStyle = { display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 } as const;

/** Row 1, right: source then age. `flex: '0 0 auto'` — these two never shrink
 *  and never wrap; the name gives way first, because a truncated name is still
 *  recognisable and a truncated chip is not. */
const cardQualifierStyle = { display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' } as const;

/**
 * ONE MUTED META LINE — the reserved height of ROW 2 (2026-09-07).
 *
 * 12.5px text at the sheet's 1.5 line-height renders an 18.75px line box, which
 * is what a card with an email measures in a lane with room to spare. Naming it
 * makes the row's floor the same number as its content instead of a literal a
 * later type change can walk away from — the argument `CHIP_HEIGHT` makes for
 * row 3, one row up.
 *
 * ⚠️ IT IS `minHeight` AND NOT `height`. A floor, not a ceiling: nothing in row
 * 2 wraps today, but a fixed height would clip whatever lands here next in
 * exactly the silent way the 88px card floor clipped this row.
 *
 * Exported so the spec asserts the row against THIS value rather than against a
 * re-typed number that could quietly stop matching it.
 */
export const CARD_META_LINE = '18.75px';

/**
 * ROW 2 — the email. ONE LINE, ALWAYS RENDERED, AND ALWAYS AT LEAST ONE LINE
 * TALL (2026-09-07, CTO: six cards boxed as badly padded, all of them cards
 * with no email row).
 *
 * `whiteSpace: nowrap` + `overflow: hidden` + ellipsis is what stops a long
 * address wrapping and making card height depend on whose mail provider has the
 * longer name. The `minHeight` is what stops the row DISAPPEARING, and it
 * answers two different failures at once:
 *
 *   · a card with no email on it — a real board state (a phone-only callback
 *     lead, a contact created down the standalone `/callback` lane before it
 *     collected an address) — used to render an empty span with no line box, so
 *     the row collapsed and the card sat ~19px shorter than its neighbours with
 *     everything below packed up against the name;
 *   · and the ellipsis's own `overflow: hidden` sets this item's automatic
 *     minimum size to ZERO, which made row 2 the one row a squeezed card could
 *     crush — see the block on `cardStyle` for how that clipped the email off
 *     cards that HAD one. `minHeight` is a declared minimum, so it replaces the
 *     automatic zero and the row is now the last thing that gives, not the
 *     first.
 *
 * Both failures are the same shape as the status slot's (row 3): an absent fact
 * collapsing its own space instead of holding it.
 */
const cardEmailRowStyle = {
  ...metaStyle,
  minHeight: CARD_META_LINE,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/** ROW 2 with no address to put in it — muted and italic, the same treatment the
 *  nameless record's "Unknown" wears one row up, so the card has ONE visual
 *  grammar for "this fact is not on file" rather than two. */
const cardNoEmailStyle = { ...cardEmailRowStyle, fontStyle: 'italic' } as const;

/**
 * IS THERE AN EMAIL ON THIS CARD? (2026-09-07)
 *
 * `DealContact.email` is typed `string` and clinic-service validates it with
 * Zod `.email()` on the way IN — but this portal type-ASSERTS the deals response
 * rather than parsing it (`get<DealList>`), so what actually reaches this
 * component is whatever the wire held: a blank string, or nothing at all on a
 * row whose contact was seeded down a lane that never captured an address. The
 * CTO's screenshots are the evidence that it happens.
 *
 * A trim, so a contact carrying a single space is treated as the empty thing it
 * is rather than rendering an invisible email row.
 */
export function cardEmail(card: DealCardShape): string | null {
  const email = card.contact.email?.trim();
  return email ? email : null;
}

/**
 * THE STATUS ROW'S FLOOR — THE TALLEST CHIP THAT CAN LAND IN IT, NOT THE
 * SHORTEST (2026-09-07).
 *
 * ⚠️ IT WAS `CHIP_HEIGHT` (22px) AND THAT LEFT THE CARD WITH THREE HEIGHTS,
 * which is a second-order version of the bug `CHIP_HEIGHT` was introduced to
 * fix. Row 3 hosts chips from TWO families and they do not agree about their
 * own height:
 *   · `statusChips`' own chips (New, the clinical status chip, the questionnaire
 *     ghosts, the park) go through `chipStyle`, which carries
 *     `minHeight: CHIP_HEIGHT` + `box-sizing: border-box` + a 1.2 line-height.
 *     Measured: 22px, bordered or filled.
 *   · `ui/kit`'s `Badge` — the ELIGIBILITY FALLBACK, and also Test and
 *     "Plan sent" — carries neither. It is `padding: 4px 10px` around an 11px
 *     line box at the sheet's inherited 1.5 line-height: 16.5 + 4 + 4 =
 *     24.5px. Measured: 24.5px.
 * So a card wearing a Badge was 2.5px taller than a card wearing a status chip,
 * and both were taller than a card with nothing in the row — 122 / 119.5 /
 * 119.5px, read off the live board. Three heights in one lane, from the row
 * that exists to make them one.
 *
 * ⚠️ AND jsdom CANNOT SEE ANY OF IT. `cardStatusSlot.spec.tsx` asserts
 * `minHeight === CHIP_HEIGHT` and passes; the CTO's screenshots are what caught
 * it, for the second wave running. When this row changes, MEASURE IT in a
 * browser — every claim about it that came from a unit test has been wrong.
 *
 * THE ESTATE FIX, so this constant can go back to being one number: teach
 * `ui/kit`'s `Badge` the same box `chipStyle` already has (`box-sizing:
 * border-box`, `minHeight: CHIP_HEIGHT`, a 1.2 line-height). It is a shared
 * primitive on a dozen screens, so it is a change of its own with its own
 * measurement pass — not something to fold into a card-padding fix. Until then
 * the CARD is the surface that has to hold one rhythm, so the floor lives here
 * and reserves the taller of the two.
 *
 * A CSS `max()` rather than a bare 24.5px, so the rule is executable instead of
 * merely commented: it still READS `CHIP_HEIGHT` from statusChips, so if that
 * constant ever grows past the Badge the floor follows it with no edit here —
 * which is the drift the whole named-constant argument is about. Both halves are
 * real: Chrome resolves the function, and jsdom keeps the string verbatim, so a
 * spec can assert the tie to `CHIP_HEIGHT` textually even though it can never
 * see the pixels.
 */
export const CARD_CHIP_ROW_MIN = `max(${CHIP_HEIGHT}, 24.5px)`;

/** ROW 3 — the status chips. ONE gap rule instead of a `marginLeft` on each
 *  chip: the per-chip margins left a stray indent whenever the first chip in
 *  the row happened not to render, which is most cards.
 *
 *  ⚠️ ALWAYS RENDERED, AND ALWAYS AT LEAST ONE CHIP TALL (2026-09-06, CTO).
 *  The row used to collapse to nothing on a card with no chips, so two cards
 *  in the same lane differed in height by whether anyone had touched the
 *  patient — the eye reads that as a layout fault long before it reads it as
 *  information. `minHeight` reserves the slot whether or not anything fills
 *  it, so a lane is a stack of identical rectangles and the CONTENT is the
 *  only thing that varies. It is a floor, not a ceiling: a genuinely busy card
 *  still wraps onto a second line, as it always has.
 *
 *  The floor is `CARD_CHIP_ROW_MIN` — see it for why `CHIP_HEIGHT` alone was
 *  2.5px short of the chip that lands here most often. */
const cardChipRowStyle = { ...metaStyle, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', minHeight: CARD_CHIP_ROW_MIN } as const;

/** ROW 4 — the created-on footer, ruled off with a hairline. The rule is what
 *  makes it a footer: without it the date is just a fourth fact, and the eye
 *  reads it on every card instead of skipping it. */
const cardFooterStyle = {
  ...dateStyle,
  borderTop: '1px solid var(--gps-border)',
  paddingTop: '5px',
  marginTop: '2px',
} as const;

/** True when the contact carries any real name part. */
export function hasName(card: DealCardShape): boolean {
  return Boolean(card.contact.firstName || card.contact.lastName);
}

/** Contact display name — email as the fallback identity (contacts key on it).
 * Used for dialog titles and search; the CARD title instead renders a muted
 * "Unknown" placeholder for a nameless record (UX Wave 4 §4 — never the
 * literal "undefined", and the email lives on its own line below).
 *
 * ⚠️ AND A THIRD FALLBACK, BECAUSE THE SECOND ONE CAN BE EMPTY (2026-09-07).
 * Once a card with no email turned out to be a real board state (see
 * `cardEmail`), a contact with neither a name nor an address made this return
 * `''` — and every caller drops the result into a SENTENCE: "Move  to
 * Callback", "Mark  as a test record", a blank drawer header. A dialog that
 * cannot name what it is about is a dialog somebody confirms against the wrong
 * record. "Unknown contact" rather than the card's bare "Unknown" because these
 * call sites are prose and the card's is a field placeholder; both say the same
 * thing about the same absence. */
export function dealName(card: DealCardShape): string {
  const parts = [card.contact.firstName, card.contact.lastName].filter((p): p is string => Boolean(p));
  if (parts.length > 0) return parts.join(' ');
  return cardEmail(card) ?? 'Unknown contact';
}

/**
 * §9 — the deal's active-round attempt count, or null when there is no
 * active call round (roundOwner null) or the server predates the field
 * (additive [CONTRACTS GAP] posture: hide the chrome rather than invent 0s).
 * Shared by the card chip, the drawer chip/CTA and the composer prompt so
 * every §9 surface reads the SAME server truth.
 */
export function activeRoundAttempts(card: DealCardShape): number | null {
  if (card.roundOwner == null || typeof card.attemptsThisRound !== 'number') return null;
  return card.attemptsThisRound;
}

/**
 * Approval visibility (2026-08-26): the clinician approved and the plan invite
 * went out, but the server's plan_sent hook does not move every card — one
 * parked for admin, or a paid card in the clinical queue, stays in its lane
 * where the approval would be invisible again. The chip carries the fact on
 * exactly those cards. `=== true` — an absent field (older server) hides the
 * chip rather than claiming "not sent" (the additive [CONTRACTS GAP] posture).
 *
 * HIDDEN WHERE THE STAGE ALREADY SAYS IT (the Plan sent lane) OR SAYS MORE.
 * "Says more" is now three distinct cases, and each is a deliberate entry:
 *
 *  - `closed_won` / `closed_lost` — the deal reached an outcome, which is a
 *    later word than "a plan went out".
 *  - AN AUTHORISED CARD IN `clinical_review` — the patient committed money and
 *    a clinician still owes the approve/reject on it. This used to be the whole
 *    of the stage `awaiting_clinical_review`; since the 2026-09-05 merge the
 *    stage cannot express it and the row state can, so this reads the SAME
 *    predicate `clinicalStatusFor` does (`isAwaitingClinicalDecision` — keyed on
 *    `holdAuthorisedAt` since 2026-09-07, when the authorisation gate turned the
 *    old `orderCaseRef` key into the marker of an UNPAID card). The
 *    unauthorised half of the merged lane is deliberately NOT hidden — it is one
 *    of the lanes the hook leaves in place, so the chip is the only record the
 *    approval has there.
 *  - `closed_duplicate` — NEW (2026-09-06), and it is the same defect
 *    `clinicalStatusFor` already guards against, wearing a different label.
 *    This chip is `tone="positive"`: a merged card carries a real `planSent`
 *    from its own superseded journey, so without the entry a record staff have
 *    deliberately taken off the working board wears a GREEN "Plan sent" badge
 *    pointing at a plan the patient is being served on the OTHER card. The
 *    clinical chip is silenced on exactly this stage for exactly this reason;
 *    the two surfaces had simply drifted.
 */
export function showPlanSentChip(card: DealCardShape): boolean {
  if (card.planSent !== true) return false;
  if (isAwaitingClinicalDecision(card)) return false;
  return !['plan_sent', 'closed_won', 'closed_lost', 'closed_duplicate'].includes(card.stage);
}

/**
 * Does this card show its stage-age cues at all — the `Nd` chip and the
 * rotting border? No, on a TERMINAL card (CTO 2026-08-27: "on closed lost or
 * won rotting number of days is not needed").
 *
 * WHY THE CLIENT SUPPRESSES IT AND THE SERVER STILL SENDS IT. `daysInStage` is
 * a true, neutral fact on a closed deal — "how long since this was won or
 * lost" — and other readers legitimately want it (the drawer states it in
 * words, and reporting reads the same field). It is only the URGENCY reading
 * that is meaningless once a deal is terminal, and clinic-service already
 * withholds exactly that: `toCards` computes `rotting` as
 * `isOpenStage(stage) && …`, so `rotting` is ALREADY false on every terminal
 * card. Nothing is wrong on the wire; what was wrong was this surface. On a
 * dense scan surface a bare number in the heading row IS an urgency cue — it
 * sits where the flag sits, and a Won card reading "412d" scans as an
 * unactioned deal. So the fix belongs here, at the rendering, and the server
 * keeps sending the fact.
 *
 * The DRAWER deliberately keeps its stage-age line on a closed deal: there the
 * number is labelled ("0d in stage", "In this stage 3 days"), it sits beside
 * the closure outcome block, and it reads as a fact rather than as a flag —
 * and its "needs attention" wording is already gated on `rotting`, which the
 * server never sets on a terminal deal.
 *
 * This does NOT trust the server's `rotting` to be false: it gates the chip
 * and the border on the STAGE, so a terminal card stays quiet even if a future
 * server (or an older one) sends `rotting: true`. A UI-only display gate, not
 * a permission gate — R41 is untouched.
 */
export function showStageAgeCue(card: DealCardShape): boolean {
  return !TERMINAL_STAGES.includes(card.stage);
}

/* ── The age chip now counts LIFETIME, not time in stage (2026-09-05) ───────
 *
 * The face answers "how long has this person been waiting on us?" — days since
 * the deal was CREATED. Hover answers "and how long here?" — which stage, since
 * when, and how many days of that lifetime it accounts for.
 *
 * ⚠️ THE NUMBER AND THE COLOUR NOW MEASURE DIFFERENT THINGS, AND THAT IS
 * DELIBERATE. `rotting` is the server's judgement on `stageEnteredAt` against
 * PIPELINE_ROT_DAYS (two, by default). Swapping the FACE to lifetime decouples
 * the pair on purpose:
 *
 *   - a 44-day-old deal that moved this morning shows "44d" in GREY;
 *   - a 3-day-old deal stuck since Monday shows "3d" in RED.
 *
 * Both readings are correct and they are answering different questions.
 * THE NUMBER IS CONTEXT; THE COLOUR IS THE SIGNAL. Keying the colour to the
 * displayed lifetime instead would turn every long-lived deal red, and an
 * alert that fires on the majority of a board is an alert nobody reads — the
 * rot warning would die of noise within a week. `cardStyle` takes the same
 * `rotting` for the card border for exactly this reason. Pinned by the named
 * test "the chip colour follows rotting, never the lifetime number".
 */

/** Whole days between `createdAt` and now, or null if the stamp will not parse. */
export function lifetimeDays(card: DealCardShape, now: number = Date.now()): number | null {
  const created = Date.parse(card.createdAt);
  if (Number.isNaN(created)) return null;
  // Clamp at 0: a clock skew between the server and this browser must read as
  // "today", never as a negative age.
  return Math.max(0, Math.floor((now - created) / 86_400_000));
}

/**
 * The age chip's face value. Falls back to the server's `daysInStage` when
 * `createdAt` will not parse — an unreadable stamp must not blank a cue that
 * the rot border is still painting beside it.
 */
export function stageAgeFace(card: DealCardShape, now: number = Date.now()): number {
  return lifetimeDays(card, now) ?? card.daysInStage;
}

/** What hover and assistive tech get: the lifetime, the stage, and the time in it. */
export function stageAgeTitle(card: DealCardShape, now: number = Date.now()): string {
  const face = stageAgeFace(card, now);
  const stage = STAGE_LABEL[card.stage] ?? card.stage;
  const enteredOn = formatUtcDate(card.stageEnteredAt);
  const inStage = `${card.daysInStage}d`;
  const age = `${face} ${face === 1 ? 'day' : 'days'} old (created ${formatUtcDate(card.createdAt)})`;
  const here = `In ${stage} since ${enteredOn} — ${inStage}`;
  // The rot sentence is gated on `rotting`, NOT on either number, so the words
  // and the colour can never tell different stories.
  return card.rotting ? `${age}. ${here}. Needs attention.` : `${age}. ${here}.`;
}

/**
 * LANE HEADER GEOMETRY (2026-09-04, CTO: the Plan-sent header "reads oddly").
 *
 * `plan_sent` carries the longest label in the estate — "Plan sent — awaiting
 * patient" — and a 280px lane cannot hold it on one line. It wrapped, the count
 * wrapped WITH it, and `alignItems: 'baseline'` then set the count against the
 * label's FIRST line while the label ran on below it, which is what looked
 * broken. Three separate things had to be true to fix it without touching the
 * wording (which is the CTO's and deliberate — it tells staff the ball is with
 * the patient):
 *
 *  - the LABEL wraps and the COUNT never does (`whiteSpace: nowrap` on a
 *    non-shrinking count, `minWidth: 0` on a label allowed to break);
 *  - `alignItems: 'flex-start'`, not `baseline` — with the label free to be two
 *    lines, the count belongs level with the label's first line, and only
 *    flex-start says that once the two spans have different heights;
 *  - EVERY header reserves two lines, so the card tops line up across all
 *    eleven lanes. A ragged top edge was the actual visual defect: ten
 *    one-line headers and one two-line header pushed a single lane's first
 *    card down, and the eye read the whole board as misaligned rather than
 *    reading one long label.
 *
 * The reservation is spelled out rather than left to `min-content` because the
 * FOCUS lane's rule is 3px and every other lane's is 1px: under the global
 * `box-sizing: border-box` a fixed `minHeight` covers the border too, so the
 * number has to clear the THICKER of the two or the focus lane would sit 2px
 * short of its neighbours. 2 x 16px line + 6px padding + 3px rule = 41px.
 */
const HEADER_LINE_HEIGHT = '16px';
const HEADER_MIN_HEIGHT = '41px';

/**
 * HOW TALL EVERY LANE IS — header, cards AND the money total. Fixed, not a cap
 * (2026-09-06).
 *
 * ⚠️ THE CAP MOVED FROM THE SCROLLER TO THE LANE, and that move is the whole
 * reason the money total is visible. It used to read `maxHeight: '62vh'` on
 * the card scroller alone, so a full lane measured 41px of header + 62vh of
 * cards + the footer below all of it — comfortably past the bottom of a laptop
 * screen. Capping the LANE instead means everything a lane has to say,
 * including its floor, is inside one bound.
 *
 * 70vh is measured, not chosen for roundness. Read off the real board at
 * 1440x900: the chrome above it (app header, breadcrumb, the Card's padding,
 * the title row and the board region's own 24px top margin) puts the board at
 * y=218, and 218 + 0.70 x 900 = 848 — the lane floor, and therefore the money
 * total, lands above the fold with the scrollbar gutter still to spare. That
 * is the property that makes the levelled row worth having.
 *
 * ⚠️ THE CARD AREA GREW, IT DID NOT SHRINK — worth stating because taking the
 * 41px header inside the cap looks like it must have cost the cards that much.
 * Measured on the same board (⚠️ 581px was measured against the STICKY-pin
 * layout that has since been replaced, because the fixed-height footer is now a
 * sibling taking its own space out of the column rather than overlaying the
 * scroll): the scroller was 581px against the old 62vh = 558px. 62vh -> 70vh
 * pays for the header and the ~32px the toolbar move returned (PipelineTab)
 * pays for the rest. The two halves of this change were sized against each
 * other.
 *
 * ⚠️ WHERE THE 630px ACTUALLY GOES, re-measured in Chrome at 1440x900 after
 * the money footer was cut to the height it actually needs (2026-09-07):
 *     41px header + 8px margin + 473px scroller + 6px margin + 102px footer
 * The footer went 116px -> 102px, so THE SCROLLER GAINED 14px — and that was
 * the point of the change (CTO: "we can actually reduce the height of this,
 * that gives more space for cards"). The 116px it replaced was never needed:
 * it had been measured against a WRAPPED term caption, a variant the wire
 * cannot produce, so every lane carried ~14px of dead space to insure against
 * a line that never wraps. See the measurement block on `LANE_FOOTER_HEIGHT`.
 * Stated here because this constant is where somebody will come looking for
 * the room if a later variant needs more: take it out of `LANE_MAX_HEIGHT`,
 * not out of `LANE_FOOTER_HEIGHT`, which is measured against its tallest
 * cell and now has 1.28px of headroom rather than 15px of slack.
 */
const LANE_MAX_HEIGHT = '70vh';

/**
 * DOES THIS LANE GET A "VIEW MORE" CONTROL? (2026-09-04)
 *
 * ⚠️ IT INHERITS `laneCount`'s TWO SUPPRESSIONS AND MUST NOT INVENT A THIRD.
 * The truncation question is already answered — and already refused in two
 * states — a few lines below; a paging control that answered it differently
 * would be a second, rival truth about the same lane.
 *
 *  - **While test records are shown**: `counts()` is hard-wired `isTest: false`
 *    while the LIST honours `includeTest`, so the page can legitimately hold
 *    MORE cards than the count. A control driven off "rendered < total" would
 *    offer a page that does not exist.
 *  - **While filtering or searching**: `items` is the post-filter list, so
 *    `rendered` means "matched", not "loaded". Offering more matches when the
 *    search has already covered every deal in the tenant would send staff
 *    hunting for someone the board has just told them is not there.
 *
 * In both states the honest answer is NO CONTROL AT ALL — the same answer the
 * count cue gives, for the same reason.
 *
 * `more` is not "rendered < total": there is no total on this wire and no
 * `hasMore` flag. A lane has another page when the page just fetched for it
 * came back FULL — see `lanePageMore` in PipelineTab, which owns the
 * bookkeeping. `status` keeps the control up through a FAILURE: a control that
 * vanishes on a failed press reads as "no more", which is a lie.
 */
export function laneViewMore(opts: {
  showTest: boolean;
  filtered: boolean;
  more: boolean;
  status?: 'loading' | 'error';
}): boolean {
  if (opts.showTest || opts.filtered) return false;
  return opts.more || opts.status === 'error';
}

/**
 * Per-lane paging, owned by PipelineTab (it owns every read) and rendered here.
 * `announcement` is the LAST arrival in words — the board holds ONE polite live
 * region for it, because a button that appends twenty-five rows below the fold
 * with no announcement tells a screen-reader user nothing happened.
 */
export interface BoardPaging {
  /** Lanes that may hold another page (the last page fetched came back full). */
  more: Partial<Record<DealStage, boolean>>;
  /** A page in flight, or a press that failed and is still retryable. */
  status: Partial<Record<DealStage, 'loading' | 'error'>>;
  announcement: string | null;
  onLoadMore: (lane: DealStage) => void;
}

/** The lane-foot paging control — full lane width, 44px minimum target. */
const viewMoreStyle = {
  width: '100%',
  minHeight: '44px',
  padding: '10px 12px',
  borderRadius: 'var(--gps-r-md)',
  border: '1px solid var(--gps-border-strong)',
  background: 'var(--gps-surface)',
  color: 'var(--gps-text)',
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
} as const;

export function PipelineBoard({
  items,
  counts,
  laneMoney,
  role,
  showTest = false,
  filtered = false,
  reasonFilter = false,
  onOpen,
  onRequestMove,
  paging,
}: {
  items: DealCardShape[];
  /** Live per-stage counts; null when the counts read failed (headers degrade to bare labels). */
  counts: DealCounts | null;
  /**
   * Per-lane MONEY behind the approved cards — ADMIN only, and null for a
   * clinician BECAUSE IT WAS NEVER FETCHED (see PipelineTab). The footer makes
   * its own role check too; see `laneMoney.tsx` for why three independent
   * gates on one number is not paranoia.
   */
  laneMoney: DealLaneMoneyResponse | null;
  /**
   * Is the board currently INCLUDING test records? Only the "N of M" partial
   * cue needs it, and only to switch itself off — see the note on `laneCount`.
   */
  showTest?: boolean;
  /**
   * Is `items` a FILTERED subset (a live search / entry-path filter) rather
   * than everything the board loaded? The partial cue switches off when it is —
   * see `laneCount`. PipelineTab owns the "your search only covers what is
   * loaded" warning, which is the honest thing to say in that state.
   */
  filtered?: boolean;
  /**
   * Is the board narrowed to cards whose patient gave a chosen drop-off reason
   * (T-2, 2026-09-11)? A SERVER-side narrowing, so unlike `filtered` it does not
   * withhold "View more" — the server pages the narrowed lane honestly. It does
   * switch off the "N of M" cue (the badge counts the whole lane, not the
   * matches), and it lets a card that has moved on still show the reason it
   * matched on. See `dropOffChipFor`.
   */
  reasonFilter?: boolean;
  /** Emphasizes the role's own focus columns (clinician → Callback; admin → Checking eligibility). */
  role: UserRole;
  onOpen: (card: DealCardShape) => void;
  /** A LEGAL drop landed — run the owning dialog/action for `target`. */
  onRequestMove: (card: DealCardShape, target: ManualTarget) => void;
  /** Per-lane "View more" state and its handler — see `laneViewMore`. */
  paging: BoardPaging;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  /**
   * ══════════════════════════════════════════════════════════════════════════
   * DRAGGING TO A LANE YOU CANNOT SEE (2026-09-22)
   * ══════════════════════════════════════════════════════════════════════════
   *
   * Eleven 280px lanes live inside ONE horizontal scroller, so the last of them
   * are off-screen on any ordinary display — and during a native HTML5 drag the
   * browser scrolls nothing for you, swallows the wheel, and gives you no way
   * to let go and scroll without dropping the card. `unreachable` is the
   * SECOND-TO-LAST lane, which is how 68 cards came to be stuck in `plan_sent`
   * with the lane that describes them impossible to drop on.
   *
   * While a drag is running, a pointer held near either edge scrolls the board.
   * The arithmetic is in `boardEdgeScroll.ts` so it can be tested without a DOM;
   * what is here is the plumbing.
   *
   * ⚠️ REACHABILITY, NEVER THE ONLY ROUTE. The drawer's ⋯ menu makes every move
   * without dragging, which is also the only route a keyboard has — a native
   * drag cannot be started from one. Do not let a future tidy-up conclude that
   * this replaces the menu row.
   */
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  /**
   * The pointer's last X, as a REF rather than state: `dragover` fires many
   * times a second and re-rendering the whole board on each one would make the
   * drag stutter — and nothing on screen depends on the value, only the loop.
   */
  const dragPointerX = useRef<number | null>(null);

  useEffect(() => {
    // No drag, nothing to do — and the effect's own cleanup is what stops the
    // loop, so a drop, a cancelled drag and an unmount are all covered by the
    // `dragId` dependency going null.
    if (!dragId) return;
    let frame = 0;
    const tick = () => {
      const el = scrollerRef.current;
      const pointerX = dragPointerX.current;
      if (el && pointerX != null) {
        const rect = el.getBoundingClientRect();
        const step = edgeScrollStep({
          pointerX,
          left: rect.left,
          right: rect.right,
          scrollLeft: el.scrollLeft,
          maxScrollLeft: el.scrollWidth - el.clientWidth,
        });
        // A zero is the ordinary case (the pointer is over the middle of the
        // board), so this is a cheap no-op frame rather than a stopped loop —
        // the pointer can re-enter a zone without anything restarting.
        if (step !== 0) el.scrollLeft += step;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    // ⚠️ A BACKSTOP, BECAUSE THE CARD CAN GO AWAY MID-DRAG. `dragId` is cleared
    // by the card's own `onDragEnd` and by a drop — but the card is a child of
    // a lane that re-renders on every board refresh, so an unmount between
    // those two events would leave `dragId` set and this loop running against
    // a drag the browser has already finished. A document-level `dragend`
    // cannot be missed that way.
    const stop = () => setDragId(null);
    document.addEventListener('dragend', stop);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('dragend', stop);
      dragPointerX.current = null;
    };
  }, [dragId]);
  const [overLane, setOverLane] = useState<DealStage | null>(null);
  const [notice, setNotice] = useState<string | null>(null);


  function handleDrop(target: DealStage) {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const card = items.find((i) => i.dealId === id);
    if (!card || card.stage === target) return;
    // ⚠️ `movesFor`, NOT `manualTarget` ALONE. Since the 2026-09-05 merge the
    // from-stage is not the whole rule: a PAID card in `clinical_review` has
    // the lane's two exits in the table and is refused both by the server (409
    // — it is the `awaiting_clinical_review` padlock, re-keyed onto
    // `orderCaseRef`). Asking the table by stage would let an operator drag a
    // paid, approved sale to Lost and get a bounce instead of an explanation.
    const move = movesFor(card, role).find((m) => m.to === target);
    if (!move) {
      // ⚠️ TWO REFUSALS, TWO SENTENCES (2026-09-22). `movesFor` filters by DESK
      // as well as by stage, so "no move" now covers a move that exists and is
      // somebody else's. Telling a clinician a card "can't be moved by hand" to
      // a lane an admin moves cards to every day sends them looking for a bug —
      // and the drawer's move buttons, which that sentence points at, will not
      // have the row either. Name the desk instead.
      const restricted = manualTarget(card.stage, target)?.roles;
      setNotice(
        restricted
          ? `Only ${restricted.join(' or ').toLowerCase()} staff can move a deal to ${STAGE_LABEL[target]}.`
          : `Deals can't be moved from ${STAGE_LABEL[card.stage]} to ${STAGE_LABEL[target]} by hand — open the deal and use its move buttons.`,
      );
      return;
    }
    setNotice(null);
    onRequestMove(card, move);
  }

  /**
   * WHAT A LANE HEADER IS ALLOWED TO CLAIM (2026-08-29).
   *
   * ⚠️ THE BOARD HOLDS ONE PAGE; THE BADGE COUNTS THE WHOLE TABLE. clinic-service
   * serves `DEALS_LIST_DEFAULT_TAKE = 50` rows for ALL ELEVEN lanes, while
   * `counts()` groups over every row in the tenant. So a header reading "12"
   * over four rendered cards is not a rounding quirk — eight of those deals are
   * not on this page at all, and the search box cannot find them either because
   * it filters what is already loaded. That combination is how a paid patient
   * sitting in Awaiting clinical review became, to the person looking for them,
   * simply absent.
   *
   * The badge is not wrong and must not be "fixed" by counting the page — the
   * total IS the operational fact staff need. What was missing is the admission
   * that the board is showing a subset, so a partial lane now reads "4 of 12".
   *
   * ⚠️ SUPPRESSED WHILE TEST RECORDS ARE SHOWN, and that is not fussiness:
   * `counts()` is hard-wired `isTest: false` (the badges ARE the conversion
   * stats) while the list read honours `includeTest`. With the toggle on, the
   * page can legitimately hold MORE cards than the badge counts, and the cue
   * would invert into a nonsense "3 of 2".
   */
  const laneCount = (lane: DealStage, rendered: number): string | null => {
    if (!counts) return null;
    const total = counts[lane] ?? 0;
    // ⚠️ NOT WHILE FILTERING. `items` is the post-filter list, so "2 of 12"
    // under an active search would read as "the board loaded 2" when it means
    // "2 matched" — turning an honest cue into a new lie. PipelineTab says the
    // true and useful thing in that state instead.
    // ⚠️ NOR UNDER A REASON FILTER: the badge counts the whole lane and the
    // lane holds only the cards that matched, so "2 of 12" would read as "the
    // board loaded two" when it means "two gave that reason".
    if (showTest || filtered || reasonFilter || rendered >= total) return String(total);
    return `${rendered} of ${total}`;
  };

  return (
    <div>
      {notice ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {notice}
        </Alert>
      ) : null}

      {/* THE PAGING ANNOUNCEMENT, AND WHY IT IS ONE REGION AND ALWAYS MOUNTED.
          A "View more" press appends twenty-five rows at the BOTTOM of a lane's
          own scroller — below the viewport, and below the button that was just
          activated. A sighted user sees the lane grow; without this a screen
          reader user gets nothing at all back from the press, which is a WCAG
          4.1.3 (Status Messages) failure.

          Mounted unconditionally, because a live region inserted at the same
          moment as its text is unreliably announced — the region has to already
          exist for the change to be a change. `polite` so it never interrupts a
          card being read, and the text NAMES THE LANE, so one region can serve
          all eleven without ambiguity. */}
      <p role="status" aria-live="polite" className="gps-sr-only">
        {paging.announcement ?? ''}
      </p>

      {/* THE JUMP BAR THAT SAT HERE IS GONE (2026-08-30, CTO: "the top-level
          filters are a bit of a mess").

          It was eleven stage chips that scrolled a lane into view — added
          because seven of the eleven 280px lanes sit off-screen in an 1100px
          frame, which is real. But it solved reachability by spending the fold:
          two wrapped rows of chips above a board people came here to work.

          The funnel dialog replaces it and does the job better. Picking a stage
          there re-reads from the SERVER scoped to that stage, so you get every
          deal in it rather than being scrolled to a lane still showing whatever
          slice of the page had loaded. Horizontal scroll remains for browsing
          all eleven at once, which is what a kanban board is.

          The lane headers keep their "N of M" cue — that one costs nothing and
          is the thing that stops a badge overstating what is on screen. */}

      {/* WHY fixed 280px tracks + the maxWidth cap (the Jira-like board scroll):
          the chain above this div is PageFrame's <main> (maxWidth 1100px) →
          PipelineTab's <Card> (a plain block <section>) → this div — all
          block-level, so each resolves to the 1100px content width. The old
          `minmax(230px, 1fr)` tracks let the grid stretch its `1fr` maxima
          instead of overflowing cleanly, and the board clipped at the viewport.
          Fixed tracks give the grid a definite 9×280px width, and
          maxWidth:'100%' + minWidth:0 pin THIS div to the Card's width, so the
          overflow lands here as a horizontal scrollbar INSIDE the Card — the
          page itself never scrolls sideways. */}
      <div
        data-testid="pipeline-board-scroll"
        ref={scrollerRef}
        // ⚠️ ON THE SCROLLER, NOT ON THE LANES. A lane's own `dragOver` fires
        // only while the pointer is over that lane, and the whole point is to
        // read a pointer parked in the GAP at the board's edge, past the last
        // lane a drop can land on. `dragover` also fires while the pointer is
        // stationary, but not reliably often, which is why the scrolling runs
        // on an animation frame and this only records where the pointer is.
        onDragOver={(event) => {
          dragPointerX.current = event.clientX;
        }}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${DEAL_STAGES.length}, 280px)`,
          gap: 'var(--gps-s-3)',
          overflowX: 'auto',
          maxWidth: '100%',
          minWidth: 0,
          // ⚠️ THE BOARD'S SCROLLBAR GETS ITS OWN LANE (2026-09-06, CTO
          // photographed it sitting ON the money total). This element scrolls
          // horizontally, so its scrollbar is drawn along the BOTTOM of its
          // padding box — which is precisely where the tallest lane's last
          // element ends. On macOS that bar is an overlay: it does not reserve
          // space, it is simply painted over whatever is underneath, and
          // `scrollbar-gutter: stable` does nothing for it. The only thing that
          // moves content out from under an overlay scrollbar is real padding.
          // 16px clears the ~15px macOS bar; it was 8px, which did not.
          //
          // This is what keeps the money total ABOVE the scrollbar rather than
          // behind it — pinning the footer without this would have swapped one
          // clipped figure for another.
          paddingBottom: 'var(--gps-s-4)',
        }}
      >
        {DEAL_STAGES.map((lane) => {
          const cards = items.filter((i) => i.stage === lane);
          // WHY `?? 0`: the portal and clinic-service deploy independently, so
          // a counts payload from an older server can predate a newly added
          // lane (plan_sent — exactly the deploy window the merge order
          // creates). DealCounts promises every key; the wire may not. An
          // absent lane renders 0, never the literal "undefined" — here (the
          // section's aria-label) and in the visible header count below.
          const heading = counts ? `${STAGE_LABEL[lane]} · ${counts[lane] ?? 0}` : STAGE_LABEL[lane];
          const isFocus = STAGE_FOCUS[lane] === role;
          const laneStatus = paging.status[lane];
          const showViewMore = laneViewMore({
            showTest,
            filtered,
            more: paging.more[lane] === true,
            status: laneStatus,
          });
          return (
            <section
              key={lane}
              aria-label={heading}
              // ── THE LANE IS A FLEX COLUMN (2026-09-06) ────────────────────
              // Header on top at its natural height, card scroller taking the
              // rest, money total pinned to the scroller's floor. The column is
              // what gives the scroller a bounded height to overflow inside —
              // without it the scroller grows to its content and nothing ever
              // sticks.
              //
              // ⚠️ EVERY LANE IS THE SAME HEIGHT, ON PURPOSE (CTO, 2026-09-06:
              // "the estimate amounts should be at the same level towards the
              // bottom in one row"). The grid's default `stretch` is what does
              // it, so this deliberately sets NO `alignSelf`.
              //
              // The first cut had `alignSelf: 'start'` and each lane sized to
              // its own content. Every total was then pinned to a DIFFERENT
              // floor — eleven figures at eleven heights, which reads as a
              // rendering accident rather than a row, and cannot be scanned
              // across. The reasoning behind it was sound but solved the wrong
              // problem: it was protecting a sticky footer INSIDE the scroller
              // from coming unstuck in a short lane. The footer is now a
              // SIBLING BELOW the scroller (see its mount point), so a lane
              // with three cards puts its total in exactly the same place as a
              // lane with forty — the slack opens up above it, inside the
              // scroller, where empty space belongs.
              style={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                // `height`, not `maxHeight`: a max still lets a short lane
                // collapse, and a collapsed lane takes its total up with it.
                height: LANE_MAX_HEIGHT,
              }}
            >
              {/* Wave 5 §7 — HubSpot-like lane header: label left, live count
                  right, over a subtle divider. The focus lane keeps its
                  heavier brand-green rule ("that will be the trigger or the
                  flag" — clinician: Callback; admin: Checking eligibility /
                  Admin follow-up). The section's aria-label keeps the
                  "<label> · <count>" wording. */}
              <h3
                style={{
                  display: 'flex',
                  // flex-start, NOT baseline — the label is allowed to be two
                  // lines now, and the count belongs level with its FIRST line
                  // (see HEADER_MIN_HEIGHT for the whole reasoning).
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '8px',
                  fontSize: '12.5px',
                  lineHeight: HEADER_LINE_HEIGHT,
                  // Two lines reserved in EVERY lane, so the card tops line up
                  // across all eleven rather than one long label pushing its
                  // own lane down.
                  minHeight: HEADER_MIN_HEIGHT,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  color: isFocus ? 'var(--gps-green-text)' : 'var(--gps-muted)',
                  borderBottom: isFocus ? '3px solid var(--gps-primary)' : '1px solid var(--gps-border)',
                  paddingBottom: '6px',
                  margin: '0 0 var(--gps-s-2)',
                }}
              >
                {/* The label WRAPS — `minWidth: 0` lets it break inside a flex
                    row rather than forcing the row wider than the lane. The
                    wording is the CTO's and is not shortened: "awaiting
                    patient" is how staff know the ball is not with them. */}
                <span style={{ minWidth: 0 }}>{STAGE_LABEL[lane]}</span>
                {counts ? (
                  <span
                    style={{
                      fontWeight: 600,
                      color: 'var(--gps-muted)',
                      fontVariantNumeric: 'tabular-nums',
                      // The COUNT never wraps and never shrinks. "20 of 24"
                      // breaking across two lines beside an already-wrapped
                      // label is what made this header read as broken.
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto',
                    }}
                  >
                    {/* "N of M" when the page holds fewer than the lane really
                        has — see `laneCount` for why the badge itself is right
                        and the admission is what was missing. */}
                    {laneCount(lane, cards.length)}
                  </span>
                ) : null}
              </h3>
              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  if (overLane !== lane) setOverLane(lane);
                }}
                onDrop={() => {
                  setOverLane(null);
                  handleDrop(lane);
                }}
                style={{
                  // ⚠️ `flex: 1 1 auto` + `minHeight: 0` — AND THE ZERO IS THE
                  // LOAD-BEARING HALF. A flex child's automatic minimum size is
                  // its CONTENT, so without `min-height: 0` this refuses to
                  // shrink below the full stack of cards: the lane blows past
                  // LANE_MAX_HEIGHT, `overflow-y: auto` never has anything to
                  // scroll, and the money total silently reverts to being the
                  // last item in the list. It is the classic flexbox trap and
                  // it fails SILENTLY — nothing errors, the layout just quietly
                  // stops doing the thing this change exists to do.
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  display: 'grid',
                  gap: '8px',
                  alignContent: 'start',
                  // Even padding on all four sides again. The bottom was
                  // deliberately zero while the total was a STICKY PIN inside
                  // here: a scroll container's content scrolls THROUGH its own
                  // bottom padding, so a 4px strip of the card behind stayed
                  // visible under the pin — the "card clipped mid-line" defect,
                  // reproduced 4px lower down. The total is a sibling below
                  // this box now, so nothing scrolls under anything and the
                  // last card wants the same breathing room as the first.
                  padding: '4px',
                  borderRadius: 'var(--gps-r-md)',
                  outline: dragId && overLane === lane ? '2px dashed var(--gps-primary)' : '2px dashed transparent',
                  background: dragId && overLane === lane ? 'var(--gps-surface-2)' : 'transparent',
                  transition: 'outline-color 120ms, background 120ms',
                }}
              >
                {cards.length === 0 ? (
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--gps-muted)' }}>No deals</p>
                ) : (
                  cards.map((card) => {
                    /**
                     * ⚠️ PER CARD, NOT PER LANE (2026-09-05). This used to be
                     * `draggableLane` — correct while every card in a lane
                     * shared its rules. The merge broke that: `clinical_review`
                     * holds both the unpaid cards that may be dragged and the
                     * PAID ones the server refuses to move (the old
                     * `awaiting_clinical_review` padlock, now keyed on
                     * `orderCaseRef`). Marking a paid card `draggable` would
                     * hand an operator a grab handle on a sale that cannot be
                     * moved, and `aria-roledescription="Draggable deal"` would
                     * announce the same falsehood to a screen reader.
                     */
                    const cardDraggable = movesFor(card, role).length > 0;
                    /* WHY a closed deal is lost — read ONCE per card and used
                     * twice below (the card's own tooltip, and the eligibility
                     * chip's when there is one). Server free text; `null` on
                     * every stage but `closed_lost`. */
                    const closedReason = cardClosedReason(card);
                    /* THE CARD'S EMAIL, OR `null` — read ONCE per card because
                     * row 2 branches on it and then renders it, and a second
                     * read is a second chance for the two halves to disagree
                     * about whether there is an address. See `cardEmail`. */
                    const email = cardEmail(card);
                    /* THE CARD'S ONE CLINICAL SLOT, decided ONCE (2026-09-06).
                     *
                     * Two derivations answer two different questions and the
                     * slot can only hold one of them, so the choice is made
                     * here rather than inside the JSX — it is also read a
                     * second time by the clinical-flags chip below, and the
                     * two must not be able to disagree about what is on
                     * screen.
                     *
                     * PRECEDENCE: a DECISION always outranks progress. An
                     * approved or rejected card keeps its existing chip on
                     * every lane; only a card nobody has ruled on yet gets the
                     * pre-decision chip, and only on the first three lanes
                     * (preDecisionChipFor returns null everywhere else, which
                     * leaves the eight later lanes rendering exactly what they
                     * rendered before this change). */
                    const status = clinicalStatusFor(card);
                    const preChip = status === 'approved' || status === 'rejected' ? null : preDecisionChipFor(card);
                    /* Is the flag fact ALREADY SPELT OUT in the slot above? If
                     * the verdict half reads FLAGGED then the warning triangle
                     * beside it is the same fact twice, an inch apart — the
                     * exact duplication the CTO struck off this card when the
                     * red closure line sat above a NOT ELIGIBLE chip. The
                     * triangle keeps rendering everywhere else, including on a
                     * card whose verdict half says NOT ELIGIBLE, because there
                     * the flag has nothing else carrying it. */
                    const verdictStatesFlags = preChip?.kind === 'review' && preChip.verdict === 'flagged';
                    return (
                    <button
                      key={card.dealId}
                      type="button"
                      draggable={cardDraggable}
                      aria-roledescription={cardDraggable ? 'Draggable deal' : undefined}
                      /* THE CLOSURE REASON'S LAST RESORT (2026-09-06). The red
                       * `CardClosedReason` line is gone from the card — it
                       * restated the eligibility chip's own verdict in danger
                       * red across two lines — and the chip's tooltip carries
                       * the reason instead. But a `closed_lost` deal need not
                       * HAVE an eligibility chip: a callback that never reached
                       * an assessment closes with `eligibility: null`, and on
                       * that card there would be nothing left to hang the
                       * reason on. Hovering the card itself still answers "why
                       * is this in Lost?" without opening the drawer. Costs no
                       * row, and a `title` on a button with text content is a
                       * description, not the accessible name — the card still
                       * announces as the patient's name. */
                      title={closedReason ?? undefined}
                      onDragStart={() => setDragId(card.dealId)}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverLane(null);
                      }}
                      onClick={() => onOpen(card)}
                      // The rot BORDER goes with the rot chip on a terminal
                      // card — a highlighted edge is the loudest urgency cue
                      // on the board, and there is no urgency left to signal.
                      style={cardStyle(card.rotting && showStageAgeCue(card), isFocus)}
                    >
                      {/* ══ ROW 1 — WHO, and the two things that qualify the
                          whole card ═══════════════════════════════════════════

                          Identity hard LEFT; the SOURCE chip and the AGE chip
                          hard RIGHT (2026-09-06, CTO). Both of those answer a
                          question about the card as a whole — "where did this
                          person come from" and "how long have they been
                          waiting" — rather than about the clinical state, so
                          they belong beside the name and not down in the tag
                          row with the verdicts. It also gives the eye a fixed
                          right-hand gutter to scan a lane down.

                          ⚠️ THE SOURCE CHIP IS OUTSIDE THE AGE CHIP'S GUARD, AND
                          THAT IS LOAD-BEARING. `showStageAgeCue` is FALSE on
                          every TERMINAL_STAGES card, so grouping the two under
                          one condition — the obvious tidy-up, since they sit in
                          the same little cluster — would silently delete
                          provenance from Won, Lost and Duplicate. The CTO's
                          instruction is that the source icon renders on ALL
                          ELEVEN stages; the age cue's suppression is a separate
                          decision about urgency (see showStageAgeCue) and must
                          not be allowed to spread to it. Pinned by the named
                          test "the source chip survives on a terminal card". */}
                      <span style={cardRowOneStyle}>
                        <span style={cardIdentityStyle}>
                          {/* THE `[Duplicate]` MARKER (2026-09-06, CTO — the
                              Asana model). WITH THE NAME, not in the tag row
                              below: the tag row is where every clinical cue
                              lives, and a bookkeeping mark competing there is
                              one more thing between a scan and the facts it is
                              hunting for. It also has to survive being quoted —
                              in a screenshot, in a ticket — and a prefix on the
                              name does, where a chip three tags along does not.
                              Deliberately NOT the strike-through `isTest` wears:
                              a test record is void data, a merged duplicate is a
                              real journey by a real patient who may have paid.
                              GROUPED with the name since the four-row layout: it
                              was previously a third child of a space-between
                              row, which floated the name it prefixes into the
                              middle of the card. */}
                          {isMergedDuplicate(card) ? <DuplicateMarker /> : null}
                          {/* `[Superseded]` (2026-09-26, S4.5) — the SYSTEM's
                              pointer, not the staff one: the patient submitted a
                              newer questionnaire and clinic closed this unpaid
                              card. Its own word, beside the name exactly where
                              `[Duplicate]` sits, because the two undo differently
                              (an ADMIN reopen vs the duplicate release) and a
                              reader who confused them would reach for the wrong
                              one. Independent of the duplicate check: a server
                              that ever sent both would show both. */}
                          {isSuperseded(card) ? <SupersededMarker /> : null}
                          {/* The contact's name, bold. A nameless historical
                              record shows a muted "Unknown" placeholder, NEVER
                              the literal "undefined". STRUCK THROUGH on a test
                              record (2026-08-26 §5) so a demo walk-through reads
                              as void data at a glance even before the muted Test
                              chip is scanned. */}
                          {/* ⚠️ `whiteSpace: 'nowrap'` IS PART OF THE ELLIPSIS,
                              NOT A SECOND OPINION ABOUT IT (2026-09-07). This
                              span carried `overflow: hidden` +
                              `textOverflow: 'ellipsis'` and neither did
                              anything, because text that is allowed to wrap
                              never overflows — the identical mistake row 2 was
                              fixed for on 2026-09-06, still live one row up.
                              So a long name WRAPPED and row 1 rendered 42px
                              instead of 26.5px (measured), which made the card
                              taller than every other card in its lane for the
                              arbitrary reason that one patient has a
                              double-barrelled surname. The layout's own note
                              says "the name gives way first, because a
                              truncated name is still recognisable and a
                              truncated chip is not" — this is the declaration
                              that finally makes that true. The full name is
                              still reachable: it is the button's own accessible
                              name, so a screen reader reads it in full and a
                              hover shows the drawer it opens. */}
                          {hasName(card) ? (
                            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(card.isTest ? { textDecoration: 'line-through' } : {}) }}>
                              {dealName(card)}
                            </span>
                          ) : (
                            <span style={{ fontSize: '14px', fontWeight: 500, fontStyle: 'italic', color: 'var(--gps-muted)', ...(card.isTest ? { textDecoration: 'line-through' } : {}) }}>Unknown</span>
                          )}
                        </span>
                        <span style={cardQualifierStyle}>
                          {/* §4 (2026-08-26): the entry-path tag as a compact
                              icon chip — title + aria-label carry the full
                              wording. Since 2026-09-06 all four provenance lanes
                              are icons (the staff SQL text badge went with the
                              rest), so this slot is one shape in one place on
                              every card in every lane. */}
                          <EntryPathChip entryPath={card.entryPath} />
                          {/* The age cue — LIFETIME on the face (2026-09-05),
                              rot on the colour. Still only on a card where
                              urgency means something: a Won or Lost card drops
                              it entirely (CTO 2026-08-27); see showStageAgeCue
                              for why the server still sends the number and only
                              this surface hides it. */}
                          {showStageAgeCue(card) ? (
                            // FACE = lifetime, COLOUR = `rotting` (time in
                            // stage). They measure different things on purpose —
                            // see the lifetimeDays block for why keying the
                            // colour to the displayed number would kill the rot
                            // alert.
                            <span role="img" aria-label={stageAgeTitle(card)} title={stageAgeTitle(card)}>
                              <Badge tone={card.rotting ? 'danger' : 'muted'}>{stageAgeFace(card)}d</Badge>
                            </span>
                          ) : null}
                        </span>
                      </span>
                      {/* ══ ROW 2 — the email, quieter than the name ═════════

                          ONE LINE, ALWAYS (2026-09-06, CTO). `overflow` +
                          `textOverflow` were already here and did nothing on
                          their own: without `whiteSpace: 'nowrap'` a long
                          address simply wrapped at the `@`, the card grew a
                          line, and the lane went ragged for the arbitrary
                          reason that one patient's mail provider has a longer
                          name than another's. The ellipsis only ever engages
                          once the text is forbidden to wrap.

                          THE WHOLE ADDRESS IS STILL REACHABLE, which is the
                          part a truncation must never cost: `title` puts it in
                          a hover tooltip AND leaves it in the accessibility
                          tree as the element's description, so a screen-reader
                          or keyboard user gets the full value even though the
                          pixels stop at the card edge. Deliberately the native
                          `title` and not a bespoke tooltip: this repo has no
                          tooltip primitive, every other truncated fact on this
                          board (the chips, the age cue, the closure reason)
                          already rides `title`, and adding a second mechanism
                          for one span would leave the card with two ways of
                          saying the same thing.

                          ⚠️ AND THE SLOT SAYS SOMETHING WHEN IT IS EMPTY
                          (2026-09-07, CTO — the badly-padded cards). Reserving
                          the line (see `cardEmailRowStyle`) is what makes every
                          card the same height; it is NOT what makes the card
                          honest. A patient with no address on file is a working
                          fact a desk needs: it is why the plan invite and the
                          restore code cannot be sent, and it is the difference
                          between "chase them by email" and "this one has to be
                          phoned". Blank reserved space says none of that, and on
                          a board where a missing row was a RENDERING fault for
                          two waves it also reads as one. So the row states it,
                          in the muted italic the nameless record's "Unknown"
                          already uses one row up — quiet enough not to compete
                          with the clinical chips below, present enough to be
                          scanned. No `title`: there is no fuller value to
                          reveal, and a tooltip repeating the visible words is
                          noise on a hover surface this dense. */}
                      {email !== null ? (
                        <span title={email} style={cardEmailRowStyle}>
                          {email}
                        </span>
                      ) : (
                        <span style={cardNoEmailStyle}>No email</span>
                      )}
                      {/* ══ ROW 2b — "Submitted as …" (2026-09-26, S4.5) ═════
                          Only when the name typed on THIS questionnaire differs
                          from the card's (the maiden/married-name rule: both are
                          hers, neither is corrected). The qualifier "(from this
                          questionnaire, not verified)" is visible TEXT in the
                          same line — never a hover-only tooltip (WCAG 1.4.13) —
                          and it WRAPS rather than truncates, because an ellipsis
                          would cut off exactly the qualifier. Absent on almost
                          every card, so it costs no row where it has nothing to
                          say. */}
                      <SubmittedAsLine card={card} style={{ fontSize: '12px' }} />
                      {/* ══ ROW 3 — the CLINICAL state of the card ═══════════
                          One gap rule rather than a `marginLeft` on every chip:
                          the old row leaked a 6px indent whenever the first chip
                          happened not to render. */}
                      <span style={cardChipRowStyle}>
                        {/* "New" — nobody has approved or rejected this card
                            yet (2026-09-05). FIRST in the row on purpose: in a
                            full lane the untouched deals are the ones a triage
                            pass is looking for, and anything that reads as a
                            status ahead of it implies someone has already been
                            here. Server-flagged only; absent renders nothing
                            rather than claiming the card HAS been worked. */}
                        {cardIsNew(card) ? <NewDealChip /> : null}
                        {/* Test-record flag (2026-08-26, reporting hygiene) —
                            MUTED on purpose: staff must be able to tell a demo
                            walk-through from a real patient at a glance, but
                            the chip must never shout over the clinical cues.
                            The DEFAULT list excludes flagged cards (the server
                            closes + hides a marked deal), so this chip only
                            ever renders inside the opt-in "Show test records"
                            view (PipelineTab, includeTest=true). Absent field
                            (older server) = a real record. */}
                        {card.isTest ? <Badge tone="muted">Test</Badge> : null}
                        {/* §3 (2026-08-26): the icon-led clinical status chip
                            replaces the bare eligibility badge wherever one of
                            the walkthrough states applies; anything else (e.g. a
                            plain auto-eligible verdict pre-plan) keeps the old
                            eligibility badge as the fallback.
                            NO SUPPRESSION HERE, deliberately (2026-08-27): the
                            card renders whatever clinicalStatusFor derives, on
                            every stage, because the derivation itself is now
                            the control. A stage-specific mute on this surface
                            would hide a wrong chip on ONE of the two surfaces
                            and leave the drawer telling the other story. If a
                            chip is wrong, it is wrong in statusChips.tsx. */}
                        {(() => {
                          /* THE PRE-DECISION SLOT LEADS (2026-09-06, CTO).
                             `status` is null on an undecided card, and null
                             used to mean "render nothing" — so a patient who
                             had submitted a clean questionnaire and a patient
                             who had never opened one were the same blank card.
                             On the first three lanes that gap is now filled;
                             `preChip` is null everywhere else and the three
                             branches below are untouched. */
                          if (preChip) {
                            if (preChip.kind === 'review') return <ClinicalStatusChip status="pending_review" variant="short" verdict={preChip.verdict} />;
                            return <QuestionnaireGhostChip state={preChip.kind} />;
                          }
                          if (status) return <ClinicalStatusChip status={status} variant="short" />;
                          if (!card.eligibility) return null;
                          const words = card.eligibility.replace(/_/g, ' ');
                          /* ⚠️ THE CLOSURE REASON RIDES THIS CHIP'S TOOLTIP
                             INSTEAD OF ITS OWN RED LINE (2026-09-06, CTO).
                             `CardClosedReason` used to print the server's free
                             text under the heading block, and on the commonest
                             Lost card that text is "not eligible (bmi_floor)" —
                             sitting directly above a chip already reading NOT
                             ELIGIBLE. The same verdict twice, the second time in
                             danger red across two lines, on the surface whose
                             whole job is density. The reason is not lost: the
                             chip's hover and accessible name carry it here, the
                             card's own `title` carries it for a closure with no
                             chip to hang on (see the button above), and the
                             drawer quotes it in full WITH ITS AUTHOR under "Why
                             this deal was lost" — which is the only place the
                             attribution has ever been safe to state. */
                          if (!closedReason) return <Badge tone={toneFor(card.eligibility)}>{words}</Badge>;
                          const withReason = `${words} — ${closedReason}`;
                          return (
                            <span role="img" aria-label={withReason} title={withReason}>
                              <Badge tone={toneFor(card.eligibility)}>{words}</Badge>
                            </span>
                          );
                        })()}
                        {/* The clinical warning mark (2026-09-05) — sits
                            with the clinical status it qualifies, not off in
                            the workflow chips. Says only THAT the assessment
                            raised flags; the flags themselves are behind the
                            click, on the review screen that acts on them. It
                            reads the SAME vector that screen reads, so the two
                            can never disagree — see ClinicalFlagsChip. */}
                        {cardHasClinicalFlags(card) && !verdictStatesFlags ? <ClinicalFlagsChip /> : null}
                        {/* Approval visibility (2026-08-26) — the plan invite
                            went out but this lane's workflow holds the card
                            here; see showPlanSentChip. */}
                        {showPlanSentChip(card) ? <Badge tone="positive">Plan sent</Badge> : null}
                        {/* Why they haven't paid (T-2, 2026-09-11) — on an
                            unpaid card, what the patient told us; a clinical
                            reason reads "Clinician to call". See
                            dropOffReasonsStaff for when a card that has moved on
                            says it too. */}
                        {(() => {
                          const dropOff = dropOffChipFor(card, reasonFilter);
                          return dropOff ? <DropOffReasonChip chip={dropOff} /> : null;
                        })()}
                        {/* Action needed (2026-08-27, stage clarity §2) — a
                            board scan finds the deals with an outstanding ask
                            without opening any of them; the drawer's banner
                            carries the ask itself. Icon + WORDS, never the
                            amber alone. Server-flagged only (R41). */}
                        {cardActionNeeded(card) ? <ActionNeededChip /> : null}
                        {/* ⚠️ EVERY ATTEMPT COUNTER IS NOW OFF THIS CARD, AND SO
                            IS THE "Call limit" WARNING (CTO, 2026-09-06 — the
                            four-row card). READ THIS BEFORE PUTTING ONE BACK.

                            The history matters because this is the THIRD pass
                            over the same square inch:
                              · 2026-09-04 — the stacked "Attempt N/3" chip went,
                                because it counted logged ROUNDS while the notes
                                beside it counted DIALS, and two numbers
                                disagreeing about one word teach staff to trust
                                neither. Its danger tone went with it, unnoticed.
                              · 2026-09-05 — per-owner "3/3 Clinician" chips
                                brought back both the count (off the two ledgers'
                                `call_attempt` rows, so DIALS at last) and the
                                warning.
                              · 2026-09-06 (earlier) — the counters went again on
                                width: the pair is 204px of a 250px row, so they
                                could never share a line and every card carrying
                                them grew a whole wrapped line. `AttemptThreshold-
                                Chip` stayed as the warning alone.
                              · 2026-09-06 (this change) — the warning goes too.

                            WHAT THAT COSTS, stated plainly rather than buried:
                            three clinician attempts AUTO-ESCALATE the callback
                            to admin and three admin attempts AUTO-PARK the deal
                            as unreachable and clear the active lead key, and the
                            BOARD now says nothing before either fires. That is a
                            real loss and it is the CTO's call: the card is a
                            scan surface, and the fact is only ever ACTED on
                            inside the drawer.

                            WHERE IT LIVES NOW, precisely — the two halves went
                            to DIFFERENT places and the difference is the point:
                            `AttemptSummary` is at the top of the drawer's
                            Activity tab, beside the call rows it tallies; the
                            WARNING is an `Alert` ABOVE THE DRAWER'S TABS,
                            rendering `attemptThreshold(...).full` verbatim,
                            because a warning behind a tab choice is a warning
                            that gets lost. `AttemptThresholdChip` is untouched
                            in stageClarity.tsx — NOT deleted, deliberately —
                            but nothing mounts it now except its own spec, so do
                            not cite it as the live warning. Everything still
                            derives from `attemptSplitChips`, so whatever
                            re-states this on any surface cannot disagree with
                            the rest.

                            IF A COUNTER EVER COMES BACK HERE it must still count
                            DIALS, not logged rounds, and it must not re-take a
                            whole row to do it. */}
                        {/* Review parked (2026-08-27, CTO) — LAST in the row,
                            and an icon only. It used to be a text pill that
                            took the clinical-status slot, so a parked card
                            went silent about the review it still owed and the
                            row wrapped onto a second line. Rendered last, it
                            qualifies the status ahead of it instead of
                            displacing it, and nothing after it can be pushed
                            down. The pause glyph carries the meaning; the full
                            wording rides title + aria-label — see
                            ReviewParkedChip for the WCAG reasoning. */}
                        {isReviewParked(card) ? <ReviewParkedChip /> : null}
                        {/* ⚠️ NO "paid, not sent to the pharmacy" CHIP HERE YET,
                            and it is a data gap rather than a decision about
                            noise — see the note at the top of statusChips.tsx.
                            The drawer's action band says it (journeyModel case
                            0b) because the drawer has the order in hand; a board
                            card carries only `paymentPosture`, which cannot tell
                            "no prescription written yet" — the ordinary state of
                            every approved order since orders #236 — from
                            "prescribed and the send failed". A chip that rode
                            every Won card would be the noise that makes a real
                            stall invisible again, which is this incident's own
                            failure mode wearing a different colour. */}
                      </span>
                      {/* ══ ROW 3b — RETURNING PATIENT (2026-09-26, S4.5) ════
                          clinic's `returning.chips`, in the server's order and
                          in the server's WORDS — an email-only chip already ends
                          "(email match — not verified)", and rendering the label
                          verbatim is what keeps the warning welded to the claim.
                          Its OWN row, not the clinical chip row: these are facts
                          about the CONTACT's other journeys, not about this
                          card's clinical state, and the sentences are long
                          enough that sharing row 3 would wrap the verdict chip
                          off its line. Absent block (an older clinic) = no row. */}
                      <ReturningChipRow card={card} />
                      {card.scheduledCallbackAt ? (
                        <span style={{ ...metaStyle, color: 'var(--gps-warn-text)', fontWeight: 600 }}>
                          ⏰ Call {formatCallWindow(card.scheduledCallbackAt)}
                        </span>
                      ) : null}
                      {/* ══ ROW 4 — the created-on footer ════════════════════
                          Ruled off with a hairline so the quietest line on the
                          card reads as a FOOTER rather than as one more fact in
                          the stack. It is orientation, not information anyone
                          acts on, and the rule is what lets a scan skip it. */}
                      {/* 2026-09-26 (S4.5): "Updated … · created …" once the
                          server stamps `lastActivityAt` — the lane's sort key
                          under the activity order, so the footer explains why a
                          card sits where it does. Byte-identical "Created …"
                          when the stamp is absent. */}
                      <span style={cardFooterStyle}>{cardFooterText(card)}</span>
                    </button>
                    );
                  })
                )}
                {/* ── The lane's paging control ──────────────────────────────
                    INSIDE this scroller on purpose: it belongs where the cards
                    END, which is where someone who has read the lane already
                    is. Put below the scroller it would float over a lane the
                    reader had not reached the bottom of.

                    Not `disabled` while loading — a disabled button drops
                    focus to the document body, so a keyboard user who pressed
                    Enter loses their place in the board just as twenty-five
                    rows arrive. `aria-busy` says the same thing and keeps the
                    control focusable; PipelineTab ignores a repeat press for a
                    lane already in flight, so nothing double-fires.

                    A FAILURE STAYS AND SAYS SO. The control silently
                    disappearing would read as "there is no more", which on a
                    board where staff are hunting a specific patient is the
                    exact lie this whole lane of work exists to remove. */}
                {showViewMore ? (
                  <div style={{ display: 'grid', gap: '6px' }}>
                    {laneStatus === 'error' ? (
                      <p
                        role="alert"
                        style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.4, color: 'var(--gps-danger-text)' }}
                      >
                        Those {BOARD_PER_LANE} did not load — the cards above are unchanged.
                      </p>
                    ) : null}
                    <button
                      type="button"
                      aria-busy={laneStatus === 'loading'}
                      // 2.5.3 Label in Name: the accessible name CONTAINS the
                      // visible label, then names the lane — eleven of these
                      // can be on screen, and "View more" alone would give a
                      // screen-reader user eleven identical buttons.
                      aria-label={
                        laneStatus === 'error'
                          ? `Try again — load ${BOARD_PER_LANE} more in ${STAGE_LABEL[lane]}`
                          : `View more (${BOARD_PER_LANE}) in ${STAGE_LABEL[lane]}`
                      }
                      onClick={() => paging.onLoadMore(lane)}
                      style={viewMoreStyle}
                    >
                      {laneStatus === 'loading'
                        ? 'Loading…'
                        : laneStatus === 'error'
                          ? 'Try again'
                          : `View more (${BOARD_PER_LANE})`}
                    </button>
                  </div>
                ) : null}
              </div>
              {/* ── The lane's MONEY footer (2026-09-06, ADMIN only) ───────────
                  A SIBLING BELOW THE SCROLLER, and the lane's last flex child —
                  so it sits on the lane's floor without needing `position:
                  sticky` at all. Every lane is the same height (see the lane
                  style above), so every one of these lands on the SAME LINE:
                  one row of totals across the foot of the board, scannable
                  left to right. That is the requirement — CTO, 2026-09-06:
                  "the estimate amounts should be at the same level towards the
                  bottom in one row".

                  ⚠️ IT HAS MOVED TWICE AND BOTH REASONS STILL APPLY. It began
                  as a sibling here, went INSIDE the scroller as a sticky pin,
                  and has come back out. The two failures it must not fall into
                  again:
                    1. Scrolling away with the cards it totals — solved by the
                       fixed lane height, which leaves the scroller bounded and
                       this element outside it.
                    2. Sitting under the page fold, beneath the board's own
                       horizontal scrollbar — the earlier sibling placement's
                       defect, and the reason it went inside. The bounded lane
                       height is what fixes that too: the lane can no longer
                       grow past the viewport, so its floor is always on screen.
                  Going back to `alignSelf: 'start'` on the lane re-breaks BOTH
                  of these and un-levels the row.

                  ⚠️ IT NOW RENDERS A CELL FOR EVERY LANE AN ADMIN SEES, and
                  the line this replaces said the opposite: "nothing for a lane
                  holding no approvals". That was the defect (CTO, 2026-09-07):
                  a lane with no approvals rendered nothing, so the strip had
                  holes punched through it and read as a rendering accident
                  rather than as eleven answers. "That entire row should be
                  dedicated to that, even if there are no values in certain
                  columns" — so an empty lane says £0, a lane the server did not
                  send says so, and a dead source still says so rather than
                  inventing a £0.

                  ⚠️ A CLINICIAN STILL GETS NOTHING — no cell, no empty cell,
                  no row. That is unchanged and it is the reason nothing here
                  wraps the footer: the gate is the FIRST line of
                  `laneMoneyFor`, so hoisting a tinted shell out of
                  `LaneMoneyFooter` to this file would put the panel on a
                  clinician's board with only its contents gated. Keep the
                  element's whole existence inside the component
                  (laneMoney.spec §1, and §5 for the gate re-asserted against
                  the always-on row). */}
              <LaneMoneyFooter
                lane={lane}
                money={laneMoney}
                role={role}
                assumedTermMonths={laneMoney?.assumedTermMonths ?? 0}
                sources={laneMoney?.sources ?? { prices: 'ok', orders: 'ok', rates: 'ok' }}
              />
            </section>
          );
        })}
      </div>
    </div>
  );
}
